import React from 'react';
import { ENTITY_TYPES } from '@adsk/bim360-matrix-react-entity-linker';
import handleFetchErrors from '@adsk/bim360-matrix-react-helpers/es/handleFetchErrors';
import { RFI_ENV_CONFIG_KEYS } from './consts';
import getEnvConfig from './helpers/getEnvConfig';
import RfiEntityBlock from './RfiEntityBlock';
import RfiEntityPickerItem from './RfiEntityPickerItem';
import RfiLinkedEntitiesCellBlock from './RfiLinkedEntitiesCellBlock';
import formatRfiName from './helpers/formatRfiName';
import RfiLinkedEntitiesCellItem from './RfiLinkedEntitiesCellItem';
import RfiCreateNewEntityForm from './RfiCreateNewEntityForm';

export default {
  entityType: ENTITY_TYPES.RFI,
  metadata: {
    containerType: 'rfi' // HQ container type - only if needed for BE calls.
  },
  createPlugin: ({ env, region, projectId, containerId, getToken }) => {
    const baseUrl = getEnvConfig(env, region, RFI_ENV_CONFIG_KEYS.RFI_BE);
    const parseEntity = entity => ({
      ...entity,
      entityType: ENTITY_TYPES.RFI,
      identifier: entity.customIdentifier
    });
    const apiCache = {
      [containerId]: null
    };
    const EntityBlock = props => {
      return <RfiEntityBlock {...props} env={env} region={region} projectId={projectId} />;
    };

    const LinkedEntitiesCellBlock = props => (
      <RfiLinkedEntitiesCellBlock {...props} env={env} region={region} projectId={projectId} />
    );

    const CreateNewEntityForm = props => (
      <RfiCreateNewEntityForm env={env} region={region} {...props} />
    );

    return {
      queryEntities: query => {
        const pageLimit = query.pageLimit;
        const searchExpression = query.searchExpression;
        const offset = query.offset || 0;
        const searchParams = new URLSearchParams();

        if (pageLimit) {
          searchParams.set('limit', pageLimit);
        }
        if (offset) {
          searchParams.set('offset', offset);
        }
        if (searchExpression) {
          searchParams.set('filter[search]', searchExpression);
        }
        searchParams.set('sort', 'customIdentifier DESC');
        const searchParamsStr = searchParams.toString();
        const queryString = searchParamsStr ? `?${searchParamsStr}` : '';
        return getToken()
          .then(token =>
            fetch(`${baseUrl}/v2/containers/${containerId}/rfis${queryString}`, {
              headers: {
                Authorization: `Bearer ${token}`
              }
            })
          )
          .then(handleFetchErrors)
          .then(res => res.json())
          .then(response => {
            const nextOffset = pageLimit ? offset + pageLimit : undefined;
            const nextPageQuery =
              nextOffset && nextOffset < response.pagination.totalResults
                ? { ...query, offset: nextOffset || offset }
                : undefined;
            return {
              data: response.results.map(parseEntity),
              nextPageQuery
            };
          });
      },
      getEntitiesByIds: entityIds =>
        getToken()
          .then(token =>
            fetch(
              `${baseUrl}/v2/containers/${containerId}/rfis?filter[id]=${entityIds.join(',')}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`
                }
              }
            )
          )
          .then(handleFetchErrors)
          .then(res => res.json())
          .then(response => ({ data: response.results.map(parseEntity) })),
      EntityBlock,
      LinkedEntitiesCellBlock,
      EntityPickerItem: RfiEntityPickerItem,
      formatEntityDisplayText: formatRfiName,
      LinkedEntitiesCellItem: RfiLinkedEntitiesCellItem,
      canCreate: () => {
        if (apiCache[containerId] !== null) {
          return Promise.resolve(apiCache[containerId]);
        }
        return getToken()
          .then(token =>
            fetch(`${baseUrl}/v2/containers/${containerId}/users/me`, {
              headers: {
                Authorization: `Bearer ${token}`
              }
            })
          )
          .then(handleFetchErrors)
          .then(res => res.json())
          .then(
            response =>
              response && response.permittedActions && !!response.permittedActions.createRfi
          )
          .then(value => {
            apiCache[containerId] = value;
            return value;
          })
          .catch(() => false);
      },
      CreateNewEntityForm,
      clearCache: () => {
        apiCache[containerId] = null;
      }
    };
  }
};
